import React from 'react'
import { Badge, Button, Select, Table, type Column } from '@/components/ds'
import { Ic } from '@/components/icons'
import { Mono, PageSizeSelect, QueryState, Val, usePageSize } from '@/components/common'
import { listQueue, listSendLog } from '@/lib/db'
import { retryFailed } from '@/lib/api'
import { useAsync } from '@/lib/useAsync'
import { useToast } from '@/lib/toast'
import { shortDateTime, timeOnly } from '@/lib/format'
import type { SendLogRow } from '@shared/types'
import type { ScreenProps } from '@/App'

/** Statuses the Worker writes into send_log. */
const EVENTS = [
  { value: 'all', label: 'All events' },
  { value: 'sent', label: 'sent' },
  { value: 'would_send', label: 'would_send (dry run)' },
  { value: 'failed', label: 'failed' },
  { value: 'skipped', label: 'skipped' },
]

function badgeFor(status: string): string {
  if (status === 'sent') return 'sent'
  if (status === 'failed') return 'failed'
  if (status === 'skipped') return 'unsubscribed'
  if (status === 'would_send') return 'scheduled'
  return 'pending'
}

export function LogsScreen({ narrow, onDataChange }: ScreenProps) {
  const toast = useToast()
  const [event, setEvent] = React.useState('all')
  const [busy, setBusy] = React.useState(false)
  const [page, setPage] = React.useState(0)
  const [pageSize, setPageSize] = usePageSize('logs', 100)

  // LIVE VIEW: new sent/failed lines appear on their own while the cron
  // delivers — a silent 5s background refresh whenever the tab is visible
  // (the hook skips hidden tabs; stale data stays rendered between polls).
  // The closure reads the current filter/page/size, so a refetch never
  // resets them.
  const logs = useAsync(() => listSendLog(event, pageSize, page * pageSize), [event, page, pageSize], {
    refreshEvery: 5000,
  })
  // Failed rows are terminal — they live here, not in the Queue.
  const failed = useAsync(() => listQueue({ statuses: ['failed'], limit: 200 }), [], {
    refreshEvery: 5000,
  })

  // A different event filter starts back at page 1 (the size choice sticks).
  React.useEffect(() => setPage(0), [event])

  const failedRows = failed.data?.rows ?? []
  const logRows = logs.data?.rows ?? []
  const logTotal = logs.data?.count ?? 0

  async function retryAll() {
    if (!failedRows.length || busy) return
    setBusy(true)
    try {
      const result = await retryFailed(failedRows.map((r) => r.id))
      toast('success', `${result.retried} queued for another attempt`, 'Attempt counter reset')
      logs.reload()
      failed.reload()
      onDataChange()
    } catch (e) {
      toast('error', 'Retry failed', e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const columns: Column<SendLogRow>[] = [
    {
      key: 'at',
      label: 'Time',
      nowrap: true,
      render: (r) => (
        <span
          className="tnum"
          title={shortDateTime(r.at)}
          style={{ fontSize: 12, color: 'var(--text-muted)' }}
        >
          {timeOnly(r.at)}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Event',
      render: (r) => <Badge status={badgeFor(r.status)}>{r.status}</Badge>,
    },
    { key: 'email', label: 'Recipient', render: (r) => <Val muted={false}>{r.email}</Val> },
    {
      key: 'smtp_response',
      label: 'SMTP response',
      render: (r) => <Mono muted>{r.smtp_response ?? '—'}</Mono>,
    },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ width: 200 }}>
          <Select options={EVENTS} value={event} onChange={setEvent} />
        </div>
        <div style={{ flex: 1 }} />
        <Button
          variant="secondary"
          icon={<Ic.retry size={14} />}
          disabled={!failedRows.length || busy}
          onClick={retryAll}
        >
          {failedRows.length ? `Retry ${failedRows.length} failed` : 'Nothing failed'}
        </Button>
      </div>

      <QueryState
        loading={logs.loading}
        error={logs.error}
        empty={logRows.length === 0}
        emptyTitle="No log entries"
        emptyDescription="The Worker writes one line per delivery attempt. While SEND_ENABLED is false it logs 'would_send' instead of delivering."
        onRetry={logs.reload}
      >
        {/* Dense log table: opaque fill, same glass frame as every other panel. */}
        <div
          className="lm-solid"
          style={{
            background: 'var(--surface-box)',
            border: '1px solid var(--glass-edge)',
            borderRadius: 'var(--glass-radius)',
            boxShadow: 'var(--glass-shadow)',
            padding: '4px 14px 0',
            overflow: 'hidden',
          }}
        >
          <div className="scroll-x">
            <Table
              rowKey="id"
              columns={columns}
              rows={logRows}
              stackBelow={narrow ? 9999 : 640}
            />
          </div>

          {/* the same footer treatment as the Leads and Queue tables */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 10,
              padding: '6px 0',
              borderTop: '1px solid var(--border-default)',
              fontSize: 12,
              color: 'var(--text-muted)',
            }}
          >
            <span style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <span>
                {logTotal === 0
                  ? 'No entries'
                  : `Showing ${page * pageSize + 1}–${Math.min(
                      (page + 1) * pageSize,
                      logTotal,
                    )} of ${logTotal.toLocaleString()}`}
              </span>
              <PageSizeSelect
                value={pageSize}
                onChange={(n) => {
                  setPageSize(n)
                  setPage(0)
                }}
              />
            </span>
            <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <Button
                variant="ghost"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Previous
              </Button>
              <span
                className="tnum"
                style={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  padding: '2px 9px',
                  borderRadius: 999,
                  background: 'var(--accent)',
                  color: 'var(--text-on-accent)',
                }}
              >
                {page + 1}
                {logTotal > 0 ? ` / ${Math.max(1, Math.ceil(logTotal / pageSize))}` : ''}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={(page + 1) * pageSize >= logTotal}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </span>
          </div>
        </div>
      </QueryState>
    </div>
  )
}
